import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useDispatchApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetHomeMetricsConfiguration,
  startGetKpiTargetCpaStatus,
  startGetTotalStatisticsAllCampaigns,
  startSetHomeMetricsConfiguration,
} from "../../actions/adsCampaigns";
import StatisticCard from "./StatisticCard";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  OVERVIEW_HOME_STATISTICS,
  OVERVIEW_HOME_STATISTICS_VIEW_MORE,
  STATISTICS_ONLY_BUZZLY_CAMPAIGNS,
  PREVIOUS_PERIOD,
  KPI_TARGET_CPA_ABOVE_PLATFORM,
  KPI_TARGET_CPA_ALERTS_TITLE,
} from "../../i18n/keysTranslations";
import { numberMetricFormat } from "../../utils/numbers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  GRAY_COLOR_TEXT_DISABLED,
  WARNING_BANNER_BG_COLOR,
  WARNING_BANNER_BORDER_COLOR,
  WARNING_BANNER_TEXT_COLOR,
} from "../../utils/colors";
import { Link } from "react-router-dom";
import {
  buildDefaultLoadingStatisticsCards,
  buildStatisticsCardsFromTotals,
} from "./buildStatisticsCardsFromTotals";
import resolveHomeKpiBreakdown from "./resolveHomeKpiBreakdown";
import { useMediaQuery, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import Button from "../Buttons/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MeasurableKpiSelector from "../../features/measurable-kpi/components/MeasurableKpiSelector";
import normalizeHomeMetricsPrimaryKpi from "../../features/measurable-kpi/utils/normalizeHomeMetricsPrimaryKpi";
import useIsAdvertisersDemo from "../../hooks/useIsAdvertisersDemo";
import { getHomeReportsDemoStatistics } from "../../utils/homeReportsDemo";

import { LaunchError } from "../../utils/errors";

const CampaignStatisticsOverview = ({
  dateRange = null,
  folderID = null,
  membershipRevision = 0,
  isLoading = false,
  headerAction = null,
  sx = {},
}) => {
  const { t } = useTranslationApp();
  const { control, watch, setValue } = useForm({
    defaultValues: {
      measurableKpi: null,
    },
  });
  const measurableKpi = watch("measurableKpi");
  const hasLoadedPrimaryKpiRef = useRef(false);
  const lastSyncedPrimaryKpiRef = useRef(undefined);

  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const dispatch = useDispatchApp();
  const isDemoAdvertiser = useIsAdvertisersDemo();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const [statisticsData, setStatisticsData] = useState(null);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [platformsAboveTargetCpa, setPlatformsAboveTargetCpa] = useState([]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const hasExplicitDateRange = !!(
    dateRange &&
    dateRange.startDateTime &&
    dateRange.endDateTime
  );

  const hasDateContext = dateRange !== null && dateRange !== undefined;
  const shouldFetchStatistics = hasDateContext;

  const totals = statisticsData?.totals || {};
  const previousTotals = statisticsData?.totalsLastPeriod || {};
  const showPreviousPeriod =
    hasExplicitDateRange && !!statisticsData?.totalsLastPeriod;

  const statisticsCards = buildStatisticsCardsFromTotals({
    totals,
    previousTotals,
    showPreviousPeriod,
    t,
  });

  const defaultLoadingCards = buildDefaultLoadingStatisticsCards(t);

  const cardsToRender =
    statisticsCards.length > 0 ? statisticsCards : defaultLoadingCards;

  const isLoadingData =
    isLoading || loadingStatistics || statisticsCards.length === 0;

  const overviewRequestEpochRef = useRef(0);

  const fetchStatistics = useCallback(async () => {
    if (!shouldFetchStatistics) {
      return;
    }

    if (isDemoAdvertiser) {
      setStatisticsData(getHomeReportsDemoStatistics());
      setLoadingStatistics(false);
      return;
    }

    const requestEpoch = ++overviewRequestEpochRef.current;
    const startP = dateRange?.startDateTime || null;
    const endP = dateRange?.endDateTime || null;

    setLoadingStatistics(true);
    try {
      const params = {
        startDateTime: startP,
        endDateTime: endP,
      };

      if (folderID) {
        params.folderID = folderID;
      }

      if (measurableKpi) {
        params.measurableKpi = measurableKpi;
      }

      if (startP && endP) {
        const breakdown = resolveHomeKpiBreakdown({
          startDateTime: startP,
          endDateTime: endP,
        });
        if (breakdown) {
          params.breakdown = breakdown;
        }
      }

      const response = await dispatch(
        startGetTotalStatisticsAllCampaigns(params)
      );

      if (requestEpoch !== overviewRequestEpochRef.current) {
        return;
      }

      if (response?.ok && response?.data) {
        setStatisticsData(response.data);
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "CampaignStatisticsOverview",
          capture: "exception",
        })
      );
      console.error("Error fetching campaign statistics:", error);
    } finally {
      if (requestEpoch === overviewRequestEpochRef.current) {
        setLoadingStatistics(false);
      }
    }
    // eslint-disable-next-line
  }, [
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    dispatch,
    folderID,
    isDemoAdvertiser,
    measurableKpi,
    membershipRevision,
    shouldFetchStatistics,
  ]);

  const scrollLeft = () => {
    if (isMobile) {
      setCurrentCardIndex((prev) => Math.max(0, prev - 1));
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (isMobile) {
      setCurrentCardIndex((prev) =>
        Math.min(cardsToRender.length - 1, prev + 1)
      );
    } else if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const calculateCardsDontFit = () => {
    const cardWidth = 280;
    const gap = 16;
    const totalCardsWidth =
      cardsToRender.length * cardWidth + (cardsToRender.length - 1) * gap;
    const containerWidth = windowWidth - 252;
    return totalCardsWidth > containerWidth;
  };

  const shouldShowScrollButtons = (() => {
    if (isMobile) {
      return statisticsCards.length > 1;
    }

    if (isTablet || isDesktop) {
      return calculateCardsDontFit();
    }

    return false;
  })();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentCardIndex(0);
  }, [measurableKpi]);

  useEffect(() => {
    const loadSavedPrimaryKpi = async () => {
      const response = await dispatch(startGetHomeMetricsConfiguration());
      let normalizedPrimaryKpi = null;

      if (response?.ok) {
        normalizedPrimaryKpi = normalizeHomeMetricsPrimaryKpi(
          response?.data?.primaryKpi
        );
      }

      lastSyncedPrimaryKpiRef.current = normalizedPrimaryKpi;
      setValue("measurableKpi", normalizedPrimaryKpi, {
        shouldDirty: false,
        shouldTouch: false,
      });
      hasLoadedPrimaryKpiRef.current = true;
    };

    loadSavedPrimaryKpi();
  }, [dispatch, setValue]);

  useEffect(() => {
    if (!hasLoadedPrimaryKpiRef.current) {
      return;
    }

    if (lastSyncedPrimaryKpiRef.current === measurableKpi) {
      return;
    }

    lastSyncedPrimaryKpiRef.current = measurableKpi;

    dispatch(
      startSetHomeMetricsConfiguration({
        metricsConfiguration: {
          primaryKpi: measurableKpi || null,
        },
      })
    );
  }, [dispatch, measurableKpi]);

  useEffect(() => {
    setStatisticsData(null);
  }, [folderID, membershipRevision]);

  useEffect(() => {
    if (isDemoAdvertiser) {
      setPlatformsAboveTargetCpa([]);
      return;
    }

    let cancelled = false;

    const loadKpiTargetCpaStatus = async () => {
      const response = await dispatch(startGetKpiTargetCpaStatus());
      if (cancelled || !response?.ok) {
        return;
      }
      const platforms = Array.isArray(response?.data?.platforms)
        ? response.data.platforms.filter((row) => row?.platform)
        : [];
      setPlatformsAboveTargetCpa(platforms);
    };

    loadKpiTargetCpaStatus();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isDemoAdvertiser, membershipRevision]);

  useEffect(() => {
    if (!shouldFetchStatistics) {
      return;
    }

    fetchStatistics();
  }, [fetchStatistics, shouldFetchStatistics]);

  const renderStatisticCard = (stat, index) => (
    <StatisticCard
      key={index}
      label={stat.label}
      value={stat.value}
      previousValue={stat.previousValue}
      currency={stat.currency}
      suffix={stat.suffix}
      platformBreakdown={stat.platformBreakdown}
      hideChangeText={true}
      hideIcon
      showPreviousPeriod={showPreviousPeriod}
      previousPeriodLabel={t(PREVIOUS_PERIOD)}
      isLoading={isLoadingData}
      growth={stat.growth}
      sparklineValues={stat.sparklineValues}
      sparklineTrend={stat.sparklineTrend}
      sx={{ height: "100%", width: "100%" }}
    />
  );

  const renderMobileView = () => (
    <Box sx={{ width: "100%", py: 1 }}>
      {renderStatisticCard(statisticsCards[currentCardIndex], 0)}
    </Box>
  );

  const renderGridView = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: `repeat(${Math.min(statisticsCards.length, 5)}, minmax(0, 1fr))`,
        },
        gap: 2,
        width: "100%",
        minWidth: 0,
        py: 1,
        px: 0,
      }}
    >
      {statisticsCards.map(renderStatisticCard)}
    </Box>
  );

  const renderScrollView = () => (
    <Box
      ref={scrollContainerRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "auto",
        overflowY: "visible",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollBehavior: "smooth",
        py: 1,
        px: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          width: "max-content",
          scrollSnapType: "x mandatory",
          alignItems: "stretch",
        }}
      >
        {statisticsCards.map((stat, index) => (
          <Box
            key={index}
            sx={{
              width: 280,
              flexShrink: 0,
              scrollSnapAlign: "start",
              display: "flex",
            }}
          >
            {renderStatisticCard(stat, index)}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 0 },
          mb: 1,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
            minHeight: 36,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t(OVERVIEW_HOME_STATISTICS)}
          </Typography>
          <Tooltip
            title={t(STATISTICS_ONLY_BUZZLY_CAMPAIGNS, { NAME_BUSINESS })}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: "12px",
                },
              },
            }}
          >
            <InfoIcon
              sx={{
                fontSize: 18,
                color: "text.secondary",
                cursor: "help",
              }}
            />
          </Tooltip>
          {headerAction ?? (
            <Typography
              component={Link}
              to="/report"
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                width: "fit-content",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              {t(OVERVIEW_HOME_STATISTICS_VIEW_MORE)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            flexWrap: "wrap",
          }}
        >
          <MeasurableKpiSelector control={control} disabled={isLoading} />
          {shouldShowScrollButtons ? (
            <>
              <Button
                color="secondary"
                size="small"
                onClick={scrollLeft}
                sx={{
                  minWidth: 32,
                  height: 32,
                  p: 0,
                  borderColor: "grey.300",
                  "&:hover": {
                    borderColor: "grey.400",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </Button>
              <Button
                color="secondary"
                size="small"
                onClick={scrollRight}
                sx={{
                  minWidth: 32,
                  height: 32,
                  p: 0,
                  borderColor: "grey.300",
                  "&:hover": {
                    borderColor: "grey.400",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
      {platformsAboveTargetCpa.length > 0 ? (
        <Box
          sx={{
            mb: 1,
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            gap: 0.25,
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: WARNING_BANNER_BG_COLOR,
            border: `1px solid ${WARNING_BANNER_BORDER_COLOR}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              width: "100%",
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: WARNING_BANNER_TEXT_COLOR,
                mt: "1px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "left",
                gap: 0.25,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  lineHeight: 1.3,
                  color: "background.paperContrastText",
                }}
              >
                {t(KPI_TARGET_CPA_ALERTS_TITLE)}
              </Typography>
              {platformsAboveTargetCpa.map((row) => (
                <Typography
                  key={row.platform}
                  sx={{
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                    color: GRAY_COLOR_TEXT_DISABLED,
                  }}
                >
                  <Trans
                    i18nKey={KPI_TARGET_CPA_ABOVE_PLATFORM}
                    values={{
                      platform: t(row.platform),
                      cpa: numberMetricFormat(row.cpa),
                      targetCpa: numberMetricFormat(row.targetCpa),
                      currency: row.currency,
                    }}
                    components={{
                      bold: (
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: "background.paperContrastText",
                          }}
                        />
                      ),
                    }}
                  />
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minWidth: 0,
        }}
      >
        {isMobile
          ? renderMobileView()
          : (isTablet || isDesktop) && !shouldShowScrollButtons
            ? renderGridView()
            : renderScrollView()}
      </Box>
    </Box>
  );
};

export default CampaignStatisticsOverview;
